import React, { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FiArrowUpRight, FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronUp, FiDownload, FiPause, FiPlay, FiRefreshCw, FiRotateCcw, FiSkipBack, FiSkipForward } from 'react-icons/fi';
import ChessPiece from './ChessPieces';
import { capturedPieces, isSameBoardSnapshot, materialBalance, moveTransition, pieceName, squareCoordinates } from './chessPresentation';
import { automaticMovePreview, destinationsForPiece, legalBoardHints } from './chessHints';

const INITIAL_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
const FILES = 'abcdefgh';
const EMPTY_MOVES = [];
export const REPLAY_MOVE_INTERVAL_MS = 2000;
export const BOARD_PREVIEW_MS = 420;
const BOARD_HINTS_STORAGE_KEY = 'chess-board-hints';

function readPosition(fen) {
  const ranks = fen.split(' ')[0].split('/');
  if (ranks.length !== 8) return readPosition(INITIAL_FEN);
  return ranks.flatMap((rank, row) => {
    const pieces = [];
    for (const character of rank) {
      if (/^[1-8]$/.test(character)) pieces.push(...Array(Number(character)).fill(null));
      else if (/^[prnbqkPRNBQK]$/.test(character)) pieces.push(character);
    }
    return Array.from({ length: 8 }, (_, column) => ({ piece: pieces[column] || null, square: `${FILES[column]}${8 - row}`, light: (row + column) % 2 === 0, row, column }));
  });
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(() => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = event => setReduced(event.matches);
    if (preference.addEventListener) preference.addEventListener('change', onChange);
    else preference.addListener(onChange);
    return () => {
      if (preference.removeEventListener) preference.removeEventListener('change', onChange);
      else preference.removeListener(onChange);
    };
  }, []);
  return reduced;
}

const ChessBoard = memo(function ChessBoard({ fen, lastMove, turn, hasState, thinking, replaying, gameId, position, moves, initialFen, connection, hintsEnabled, onHintsChange }) {
  const previous = useRef(null);
  const sequence = useRef(0);
  const [transition, setTransition] = useState(null);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const reducedMotion = useReducedMotion();
  useLayoutEffect(() => {
    const next = { fen, hasState, replaying, gameId, position, moves, initialFen, connection };
    if (isSameBoardSnapshot(previous.current, next) && previous.current.replaying === replaying) {
      previous.current = next;
      if (reducedMotion) setTransition(null);
      else if (!hintsEnabled) setTransition(current => current && current.phase === 'preview' ? { ...current, phase: 'moving' } : current);
      if (!hintsEnabled) setSelectedSquare(null);
      return;
    }
    const motion = reducedMotion ? null : moveTransition(previous.current, next);
    const preview = motion && hintsEnabled ? automaticMovePreview(previous.current, next, motion) : null;
    previous.current = next;
    sequence.current += 1;
    setSelectedSquare(null);
    setTransition(motion ? { ...motion, id: sequence.current, phase: preview ? 'preview' : 'moving', preview } : null);
  }, [fen, hasState, replaying, gameId, position, moves, initialFen, connection, reducedMotion, hintsEnabled]);
  useEffect(() => {
    if (!transition) return undefined;
    if (transition.phase === 'preview') {
      const previewTimeout = window.setTimeout(() => setTransition(current => current && current.id === transition.id ? { ...current, phase: 'moving' } : current), BOARD_PREVIEW_MS);
      return () => window.clearTimeout(previewTimeout);
    }
    // Animation-end is the primary cleanup; this also handles hidden tabs or
    // browser interruptions so a transient piece can never remain on the board.
    const timeout = window.setTimeout(() => setTransition(current => current && current.id === transition.id ? null : current), 800);
    return () => window.clearTimeout(timeout);
  }, [transition]);

  const previewing = !!(transition && transition.phase === 'preview');
  const moving = transition && !previewing ? transition : null;
  const displayFen = previewing ? transition.preview.fen : fen;
  const hints = useMemo(() => legalBoardHints(displayFen), [displayFen]);
  const squares = useMemo(() => readPosition(displayFen), [displayFen]);
  const inspectedSquare = hintsEnabled ? previewing ? transition.preview.square : selectedSquare : null;
  const destinations = hintsEnabled ? previewing ? transition.preview.destinations : destinationsForPiece(hints, selectedSquare) : EMPTY_MOVES;
  const destinationBySquare = new Map(destinations.map(destination => [destination.square, destination]));
  const checkedKing = hintsEnabled && hasState ? hints.checkedKing : null;
  const selectedPiece = squares.find(square => square.square === inspectedSquare);
  let hintMessage = 'Board hints are off.';
  let hintSummary = 'Turn on hints to inspect legal moves.';
  if (hintsEnabled) {
    if (!hasState) {
      hintMessage = 'Legal move hints will appear when the saved game connects.';
      hintSummary = 'Waiting for the saved position.';
    } else if (!hints.turn) {
      hintMessage = hintSummary = 'Legal move hints are unavailable for this saved position.';
    } else if (selectedPiece && selectedPiece.piece) {
      const name = pieceName(selectedPiece.piece);
      const targets = destinations.map(destination => `${destination.square}${destination.capture ? ' (capture)' : ''}`).join(', ');
      hintMessage = `${name} on ${inspectedSquare}: ${destinations.length ? `legal moves to ${targets}` : 'no legal moves'}.${previewing ? ' About to move.' : ''}`;
      hintSummary = previewing ? `${name} · next move` : `${name} on ${inspectedSquare} · ${destinations.length} legal ${destinations.length === 1 ? 'move' : 'moves'}`;
    } else {
      hintMessage = hintSummary = `Select a ${hints.turn === 'b' ? 'Black' : 'White'} piece to see legal moves.`;
    }
  }
  const inspectionPieces = hintsEnabled && hasState && !transition ? squares.filter(square => square.piece && (square.piece === square.piece.toUpperCase() ? 'w' : 'b') === hints.turn) : [];
  function selectPiece(square) { setSelectedSquare(current => current === square ? null : square); }
  const movedSquares = lastMove && typeof lastMove.uci === 'string' ? [lastMove.uci.slice(0, 2), lastMove.uci.slice(2, 4)] : [];
  return (
    <>
    <div className="board-hints-toolbar">
      <button type="button" className="board-hints-switch" role="switch" aria-checked={hintsEnabled} aria-label="Board hints" aria-describedby="board-hints-description" onClick={onHintsChange}><span className="hint-switch-track" aria-hidden="true"><span /></span>Board hints</button>
      {checkedKing ? <span className="board-check-status">{hints.turn === 'b' ? 'Black' : 'White'} in check</span> : hintsEnabled ? <span className="board-hints-key" aria-hidden="true"><span className="hint-key-dot" />Legal moves</span> : null}
    </div>
    <div className={`board-frame ${thinking ? 'board-thinking' : ''}`}>
      <div className="chessboard" onKeyDown={event => { if (event.key === 'Escape') setSelectedSquare(null); }}>
        <div className="board-visual-position" role="img" aria-label={`${replaying ? 'Recorded chess game replay' : hasState ? 'Shared live chess board' : 'Chess starting position, connecting to the shared game'}. White at the bottom. ${hasState ? `${(previewing ? hints.turn : turn) === 'b' ? 'Black' : 'White'} to move.` : ''}${lastMove ? ` ${previewing ? 'Upcoming' : 'Last'} move: ${lastMove.san}.` : ''}${checkedKing ? ` ${hints.turn === 'b' ? 'Black' : 'White'} king in check.` : ''}`}>
        {squares.map(({ piece, square, light, row, column }) => (
          <div key={square} className={`chess-square ${light ? 'light-square' : 'dark-square'} ${movedSquares.includes(square) ? 'last-move-square' : ''} ${square === inspectedSquare ? 'hint-selected-square' : ''} ${square === checkedKing ? 'king-in-check' : ''}`} aria-hidden="true">
            {column === 0 ? <span className="rank-label">{8 - row}</span> : null}
            {row === 7 ? <span className="file-label">{FILES[column]}</span> : null}
            {piece ? <span className={`square-piece ${moving && moving.hiddenSquares.includes(square) ? 'piece-hidden-during-move' : ''}`}><ChessPiece piece={piece} /></span> : null}
            {destinationBySquare.has(square) ? <span className={`legal-destination ${destinationBySquare.get(square).capture ? 'legal-capture-ring' : 'legal-move-dot'}`} data-hint-square={square} /> : null}
          </div>
        ))}
        {moving ? <div className="piece-animation-layer" aria-hidden="true" key={moving.id}>
          {moving.capture ? <div className={`piece-motion piece-capture ${moving.capture.restoring ? 'piece-restoring' : 'piece-disappearing'}`} style={{ left: `${squareCoordinates(moving.capture.square).file * 12.5}%`, top: `${squareCoordinates(moving.capture.square).rank * 12.5}%` }}><ChessPiece piece={moving.capture.piece} /></div> : null}
          {moving.motions.map(motion => {
            const from = squareCoordinates(motion.from);
            const to = squareCoordinates(motion.to);
            return <div key={motion.id} className="piece-motion piece-gliding" data-motion={motion.id} data-from={motion.from} data-to={motion.to} style={{ left: `${from.file * 12.5}%`, top: `${from.rank * 12.5}%`, '--delta-file': to.file - from.file, '--delta-rank': to.rank - from.rank }} onAnimationEnd={motion.id === 'main' ? () => setTransition(current => current && current.id === moving.id ? null : current) : undefined}><ChessPiece piece={motion.piece} /></div>;
          })}
        </div> : null}
        </div>
        {inspectionPieces.length ? <div className="board-inspection-layer" role="group" aria-label="Inspect legal moves">
          {inspectionPieces.map(({ piece, square, row, column }) => <button type="button" key={square} className="piece-inspect-button" style={{ left: `${column * 12.5}%`, top: `${row * 12.5}%` }} aria-label={`Show legal moves for ${pieceName(piece)} on ${square}`} aria-pressed={selectedSquare === square} aria-describedby="board-hints-description" onClick={() => selectPiece(square)} />)}
        </div> : null}
      </div>
    </div>
    <p className="board-hints-description" id="board-hints-description" role="status" aria-live="polite" aria-atomic="true"><span aria-hidden="true">{hintSummary}</span><span className="sr-only">{hintMessage}</span></p>
    </>
  );
});

function useSharedGame() {
  const [state, setState] = useState(null);
  const [connection, setConnection] = useState('connecting');
  const [unavailable, setUnavailable] = useState(false);
  useEffect(() => {
    let disposed = false;
    let socket = null;
    let reconnectTimer;
    let reconnectDelay = 1000;
    let hasReceivedState = false;
    let fetching = false;
    const requests = new Set();
    function acceptState(data) {
      if (disposed || !data || typeof data.fen !== 'string' || !data.gameId || !data.players) return false;
      hasReceivedState = true;
      setState(previous => previous && Number(previous.updatedAt) > Number(data.updatedAt) ? previous : data);
      setUnavailable(false);
      return true;
    }
    async function fetchState() {
      if (disposed || fetching || document.hidden) return;
      fetching = true;
      const controller = new AbortController();
      requests.add(controller);
      const timeout = window.setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch('/api/chess', { signal: controller.signal, cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Unavailable');
        if (!acceptState(await response.json())) throw new Error('Invalid game state');
        if (!socket || socket.readyState !== WebSocket.OPEN) setConnection('polling');
      } catch (error) {
        if (!disposed) { setUnavailable(true); if (!hasReceivedState) setConnection('offline'); }
      } finally { clearTimeout(timeout); requests.delete(controller); fetching = false; }
    }
    function scheduleReconnect() {
      clearTimeout(reconnectTimer);
      if (disposed || document.hidden) return;
      reconnectTimer = window.setTimeout(connect, reconnectDelay);
      reconnectDelay = Math.min(reconnectDelay * 2, 30000);
    }
    function connect() {
      if (disposed || document.hidden || typeof WebSocket === 'undefined' || (socket && socket.readyState < WebSocket.CLOSING)) return;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      try { socket = new WebSocket(`${protocol}//${window.location.host}/api/chess/ws`); }
      catch (error) { scheduleReconnect(); return; }
      socket.onopen = () => { reconnectDelay = 1000; };
      socket.onmessage = event => {
        try { const data = JSON.parse(event.data); if (acceptState(data.type === 'state' ? data.state : data)) setConnection('live'); }
        catch (error) { /* Preserve the last known state when a message is malformed. */ }
      };
      socket.onclose = () => { if (!disposed) { setConnection(hasReceivedState ? 'reconnecting' : 'connecting'); scheduleReconnect(); } };
      socket.onerror = () => socket && socket.close();
    }
    function onVisibilityChange() {
      if (document.hidden) { clearTimeout(reconnectTimer); if (socket) socket.close(); }
      else { fetchState(); connect(); }
    }
    fetchState();
    connect();
    const poll = window.setInterval(() => { if (!socket || socket.readyState !== WebSocket.OPEN) fetchState(); }, 20000);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      disposed = true;
      clearTimeout(reconnectTimer);
      clearInterval(poll);
      requests.forEach(controller => controller.abort());
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (socket) socket.close();
    };
  }, []);
  return { state, connection, unavailable };
}

function formatModel(model) {
  if (typeof model !== 'string') return 'Connecting…';
  const names = {
    'apodex/apodex-1.1-mini': 'Apodex 1.1 Mini',
    'nvidia/nemotron-3-super-120b-a12b': 'Nemotron 3 Super 120B A12B',
    'qwen/qwen3.7-flash': 'Qwen3.7 Flash',
    'deepseek/deepseek-v4.1-flash': 'DeepSeek V4.1 Flash',
    'inclusionai/ling-3.0-flash': 'Ling3.0 Flash',
    'inclusionai/ling-3.1-flash': 'Ling3.1 Flash',
  };
  const id = model.replace(/:free$/, '');
  return names[id] || id.split('/').pop().replace(/-/g, ' ').replace(/\b[a-z]/g, character => character.toUpperCase());
}

function CapturedPieces({ side, captures }) {
  const label = `Captured by ${side === 'w' ? 'White' : 'Black'}`;
  return (
    <div className="captured-panel" role="group" aria-label={label}>
      <div className="captured-heading"><h4>{label}</h4>{captures.length ? <span>{captures.length}</span> : null}</div>
      {captures.length ? <ul className="captured-pieces" aria-label={label}>{captures.map((capture, index) => <li key={`${capture.ply}-${capture.square}-${index}`} aria-label={`${pieceName(capture.piece)} captured on move ${capture.ply}`} title={pieceName(capture.piece)}><ChessPiece piece={capture.piece} /></li>)}</ul> : <p className="captured-empty">None yet</p>}
    </div>
  );
}

function AgentCard({ side, player, active, status, captures }) {
  const considering = active && !!status && status !== 'replay' && status !== 'finished';
  return (
    <article className={`chess-agent player-row agent-${side} ${active ? 'agent-active' : ''}`} aria-label={`${formatModel(player && player.model)}, playing ${side === 'w' ? 'white' : 'black'}`}>
      <span className="player-side-icon"><ChessPiece piece={side === 'w' ? 'K' : 'k'} /></span>
      <div className="player-identity"><h3>{formatModel(player && player.model)}</h3><div className="agent-presence"><span className="player-color">{side === 'w' ? 'White' : 'Black'}</span><span className={`presence-dot ${considering ? 'is-thinking' : ''}`} />{status === 'replay' ? active ? 'Reviewing position' : 'Recorded game' : status === 'finished' ? 'Game finished' : considering ? 'Considering' : status === 'waiting_for_key' ? 'Waiting for a key' : status ? 'Waiting their turn' : 'Connecting'}</div></div>
      <CapturedPieces side={side} captures={captures} />
    </article>
  );
}

function statusMessage(state, connection, unavailable) {
  if (unavailable) return state ? 'Connection interrupted. Showing the last saved position; reconnecting automatically.' : 'The chess room is temporarily unavailable. The saved game will return when the server reconnects.';
  if (!state) return 'Connecting to the shared chess room…';
  if (state.status === 'waiting_for_key') return 'The board is ready. The agents are waiting for their OpenRouter key.';
  if (state.status === 'rate_limited') return state.modelStatus || 'The model service is busy. The saved game will retry automatically.';
  if (state.status === 'error') return state.modelStatus || 'The model is unavailable. The saved game is paused.';
  if (state.status === 'finished') return state.result ? `Game over · ${state.result}. This game’s moves are saved.` : 'Game over. This game’s moves are saved.';
  if (connection === 'reconnecting') return 'Reconnecting to live updates. The server is keeping the game going.';
  const player = state.activePlayer && (!state.activePlayer.side || state.activePlayer.side === state.turn) ? state.activePlayer : state.players[state.turn];
  return `${formatModel(player && player.model)} is considering a move.`;
}

function downloadPGN(game) {
  if (!game || !game.pgn) return;
  const url = URL.createObjectURL(new Blob([game.pgn], { type: 'application/x-chess-pgn;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `agents-${game.gameId}.pgn`;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function ReplayControls({ position, moves, playing, onTogglePlayback, onPositionChange, onReturnLive }) {
  const total = moves.length;
  const atEnd = total > 0 && position === total;
  const playbackLabel = atEnd ? 'Play replay again' : playing ? 'Pause replay' : 'Play replay';
  const currentMove = position > 0 ? moves[position - 1] : null;
  const positionLabel = currentMove ? `Move ${position} of ${total}: ${currentMove.san}` : `Starting position, ${total} recorded ${total === 1 ? 'move' : 'moves'}`;
  return (
    <div className="replay-controls" aria-label="Recorded game replay controls">
      <div className="replay-heading"><span className="replay-label">REPLAY</span><span className="replay-position" role="status" aria-atomic="true">{position === 0 ? 'Starting position' : `Move ${position} of ${total} · ${currentMove.san}`}</span><button className="return-live-button" onClick={onReturnLive}><span className="connection-dot connection-live" aria-hidden="true" />Return to live</button></div>
      <div className="replay-actions">
        <label className="replay-slider"><span className="sr-only">Choose a recorded move</span><input type="range" min="0" max={total} step="1" value={position} onPointerDown={() => onPositionChange(position)} onChange={event => onPositionChange(Number(event.target.value))} disabled={!total} aria-valuetext={positionLabel} /></label>
        <button onClick={() => onPositionChange(0)} disabled={position === 0} aria-label="First position" title="First position"><FiSkipBack aria-hidden="true" /></button>
        <button onClick={() => onPositionChange(position - 1)} disabled={position === 0} aria-label="Previous move" title="Previous move"><FiChevronLeft aria-hidden="true" /></button>
        <button className="replay-play-button" onClick={onTogglePlayback} disabled={!total} aria-label={playbackLabel} aria-pressed={playing} title={playbackLabel}>{playing ? <FiPause aria-hidden="true" /> : <FiPlay aria-hidden="true" />}<span className="replay-play-label">{atEnd ? 'Play again' : playing ? 'Pause' : 'Play'}</span></button>
        <button onClick={() => onPositionChange(position + 1)} disabled={position === total} aria-label="Next move" title="Next move"><FiChevronRight aria-hidden="true" /></button>
        <button onClick={() => onPositionChange(total)} disabled={position === total} aria-label="Last position" title="Last position"><FiSkipForward aria-hidden="true" /></button>
      </div>
    </div>
  );
}

function SavedGames({ liveGameId, selectedGameId, replayGame, onReplayGame }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingGameId, setLoadingGameId] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const historyController = useRef(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; if (historyController.current) historyController.current.abort(); }, []);
  async function fetchHistory(gameId, selectGame, cursor = null) {
    if (historyController.current) historyController.current.abort();
    setLoadingHistory(true);
    setLoadingGameId(selectGame ? gameId : null);
    setLoadingOlder(!!cursor);
    setHistoryError(false);
    const controller = new AbortController();
    historyController.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    try {
      const query = new URLSearchParams({ gameId });
      if (cursor) query.set('cursor', cursor);
      const response = await fetch(`/api/chess/history?${query.toString()}`, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unavailable');
      const data = await response.json();
      if (!Array.isArray(data.games) || (selectGame && (!data.game || !data.game.players || !Array.isArray(data.game.moves) || !data.game.moves.every(move => typeof move.fen === 'string')))) throw new Error('Missing recorded game');
      if (mounted.current && historyController.current === controller) {
        setHistory(previous => {
          if (selectGame && previous) return { ...previous, game: data.game };
          if (cursor && previous) {
            const games = [...new Map([...previous.games, ...data.games].map(game => [game.gameId, game])).values()];
            return { ...data, games, game: previous.game };
          }
          return data;
        });
        if (selectGame) onReplayGame(data.game);
      }
    } catch (error) { if (mounted.current && historyController.current === controller) setHistoryError(true); }
    finally { clearTimeout(timeout); if (mounted.current && historyController.current === controller) { setLoadingHistory(false); setLoadingGameId(null); setLoadingOlder(false); } }
  }
  function toggleHistory() {
    if (open) { setOpen(false); return; }
    setOpen(true);
    fetchHistory(liveGameId, false);
  }
  const loadedGame = history && history.game;
  const exportGame = replayGame ? replayGame.pgn ? replayGame : loadedGame && loadedGame.gameId === replayGame.gameId ? loadedGame : null : loadedGame && loadedGame.gameId === liveGameId ? loadedGame : null;
  return (
    <div className="saved-games-section">
      <button className="text-button history-button" onClick={toggleHistory} aria-expanded={open}>{open ? 'Close saved games' : 'View saved games'}{open ? <FiChevronUp aria-hidden="true" /> : <FiArrowUpRight aria-hidden="true" />}</button>
      {open ? <div className="saved-history">
        <div className="saved-history-heading"><p>Saved games</p><button className="text-button" onClick={() => fetchHistory(liveGameId, false)} disabled={loadingHistory} aria-label="Refresh saved games">Refresh <FiRefreshCw aria-hidden="true" /></button></div>
        {loadingHistory ? <p className="history-loading" role="status">{loadingGameId ? 'Loading recorded game…' : loadingOlder ? 'Loading older games…' : 'Loading saved games…'}</p> : null}
        {historyError ? <p className="history-error" role="status">The saved games couldn’t load. Please try again.</p> : null}
        {history && history.games.length ? <ul className="saved-games-list" aria-label="Choose a saved game to replay">{history.games.map(game => <li key={game.gameId}>
          <button className={`saved-game ${selectedGameId === game.gameId ? 'saved-game-selected' : ''}`} onClick={() => fetchHistory(game.gameId, true)} disabled={loadingHistory} aria-pressed={selectedGameId === game.gameId} aria-label={`Replay ${game.gameId === liveGameId ? 'current' : 'saved'} game ${game.gameId.slice(0, 8)}, ${game.moveCount} recorded moves${game.result ? `, result ${game.result}` : ''}`}>
            <span className="saved-game-title">{new Date(game.startedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}<span>{game.gameId === liveGameId ? 'CURRENT' : game.gameId.slice(0, 8).toUpperCase()}</span></span>
            <span className="saved-game-detail">{game.moveCount} {game.moveCount === 1 ? 'move' : 'moves'} · {game.result || 'In progress'}<FiArrowUpRight aria-hidden="true" /></span>
          </button>
        </li>)}</ul> : history ? <p className="history-loading">No saved games yet.</p> : null}
        {history && history.nextCursor ? <button className="text-button load-older-button" onClick={() => fetchHistory(liveGameId, false, history.nextCursor)} disabled={loadingHistory}>{loadingOlder ? 'Loading older games…' : 'Load older games'}<FiChevronDown aria-hidden="true" /></button> : null}
        {exportGame && exportGame.pgn ? <button className="text-button pgn-button" onClick={() => downloadPGN(exportGame)}><FiDownload aria-hidden="true" />Download {replayGame ? 'replayed' : 'current'} game as PGN</button> : null}
      </div> : null}
    </div>
  );
}

function MoveLog({ moves, liveGameId, hasState, replay, selectedPosition, onSelectMove, onReplayGame }) {
  const scrollRef = useRef(null);
  const pairs = [];
  for (let i = 0; i < moves.length; i += 2) pairs.push([moves[i], moves[i + 1]]);
  const lastMove = replay ? selectedPosition > 0 ? moves[selectedPosition - 1] : null : moves[moves.length - 1];
  useEffect(() => { if (scrollRef.current && !replay) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [moves.length, replay]);
  useEffect(() => {
    if (!replay || !scrollRef.current) return;
    const selected = scrollRef.current.querySelector('[aria-current="step"]');
    if (selected) {
      const top = selected.offsetTop;
      if (top < scrollRef.current.scrollTop || top + selected.offsetHeight > scrollRef.current.scrollTop + scrollRef.current.clientHeight) scrollRef.current.scrollTop = Math.max(0, top - scrollRef.current.clientHeight / 2);
    } else if (!selectedPosition) scrollRef.current.scrollTop = 0;
  }, [selectedPosition, replay]);
  return (
    <aside className="move-panel" aria-label="Server recorded chess moves">
      <div className="panel-heading"><h3>Moves</h3><span className="move-count">{moves.length} {moves.length === 1 ? 'move' : 'moves'}</span></div>
      <p className="panel-description">Select a move to review the position.</p>
      <div className="move-table-header" aria-hidden="true"><span>#</span><span>White</span><span>Black</span></div>
      <div className="move-scroll" ref={scrollRef} tabIndex={moves.length ? 0 : -1} aria-label="Scroll move history">
        {pairs.length ? <ol className="move-list">{pairs.map(([white, black], index) => <li key={white.ply || index}><span>{index + 1}.</span><button className="move-button" onClick={() => onSelectMove(index * 2 + 1)} aria-label={`Replay move ${index * 2 + 1}: white ${white.san}`} aria-current={replay && selectedPosition === index * 2 + 1 ? 'step' : undefined}>{white.san}</button>{black ? <button className="move-button" onClick={() => onSelectMove(index * 2 + 2)} aria-label={`Replay move ${index * 2 + 2}: black ${black.san}`} aria-current={replay && selectedPosition === index * 2 + 2 ? 'step' : undefined}>{black.san}</button> : <span className="missing-move">—</span>}</li>)}</ol> : <div className="empty-moves"><p>{hasState ? 'No moves yet.' : 'Finding the saved game…'}</p><small>{hasState ? 'The opening move will appear here.' : 'Connecting to the saved position.'}</small></div>}
      </div>
      <div className="last-move-note" aria-live="polite" aria-atomic="true">{lastMove ? <><span className="note-label">{replay ? 'SELECTED MOVE' : 'LAST MOVE'}</span><strong>{lastMove.san}</strong>{lastMove.comment ? <p>{lastMove.comment}</p> : null}</> : replay ? <><span className="note-label">STARTING POSITION</span><p>Select a move or use the arrows to step through the game.</p></> : <><span className="note-label">OPENING POSITION</span><p>No moves recorded yet.</p></>}</div>
      {hasState ? <SavedGames liveGameId={liveGameId} selectedGameId={replay && replay.game.gameId} replayGame={replay && replay.game} onReplayGame={onReplayGame} /> : null}
    </aside>
  );
}

function RoomStats({ stats, position, gameMoveCount, moveNumber, replaying, balance, hasGame }) {
  const count = key => stats && Number.isFinite(stats[key]) ? stats[key].toLocaleString() : '—';
  const material = !hasGame ? '—' : balance === 0 ? 'Even' : `${balance > 0 ? 'White' : 'Black'} +${Math.abs(balance)}`;
  return (
    <section className="room-statistics" aria-label="Room statistics">
      <div className="stats-heading"><h2>Scoreboard</h2><span>All time</span></div>
      <dl className="room-stats-grid"><div><dt>Completed games</dt><dd>{count('completedGames')}</dd></div><div><dt>Total moves</dt><dd>{count('totalMoves')}</dd></div></dl>
      <div className="results-summary"><span className="stat-section-label">Results</span><dl><div><dt>White wins</dt><dd>{count('whiteWins')}</dd></div><div><dt>Black wins</dt><dd>{count('blackWins')}</dd></div><div><dt>Draws</dt><dd>{count('draws')}</dd></div></dl></div>
      <div className="position-statistics"><span>{replaying ? 'Position' : 'Move'}<strong>{hasGame ? replaying ? `${position} / ${gameMoveCount}` : moveNumber : '—'}</strong></span><span title="Material points: pawn 1, knight and bishop 3, rook 5, queen 9">Material<strong>{material}</strong></span></div>
    </section>
  );
}

export default function ChessLounge() {
  const { state, connection, unavailable } = useSharedGame();
  const [replay, setReplay] = useState(null);
  const [hintsEnabled, setHintsEnabled] = useState(() => {
    try { return window.localStorage.getItem(BOARD_HINTS_STORAGE_KEY) !== 'false'; }
    catch (error) { return true; }
  });
  const replaySequence = useRef(0);
  const boardColumnRef = useRef(null);
  const game = replay ? replay.game : state;
  const moves = game && Array.isArray(game.moves) ? game.moves : EMPTY_MOVES;
  const position = replay ? Math.max(0, Math.min(replay.position, moves.length)) : moves.length;
  const lastMove = position > 0 ? moves[position - 1] : null;
  const fen = replay ? lastMove ? lastMove.fen : game.initialFen || INITIAL_FEN : game ? game.fen : INITIAL_FEN;
  const turn = replay ? fen.split(' ')[1] : game && game.turn;
  const status = replay ? 'replay' : state && state.status;
  const initialFen = (game && game.initialFen) || INITIAL_FEN;
  const captures = useMemo(() => capturedPieces(initialFen, moves, position), [initialFen, moves, position]);
  const balance = useMemo(() => materialBalance(fen), [fen]);
  const replaySession = replay && replay.session;
  const replayPosition = replay && replay.position;
  const replayPlaying = !!(replay && replay.playing);
  const replayMoveCount = replay ? replay.game.moves.length : 0;

  useEffect(() => {
    if (!replayPlaying || replayPosition >= replayMoveCount || document.hidden) return undefined;
    const timeout = window.setTimeout(() => {
      setReplay(previous => {
        if (!previous || !previous.playing || previous.session !== replaySession || previous.position !== replayPosition) return previous;
        if (document.hidden) return { ...previous, playing: false };
        const nextPosition = Math.min(previous.position + 1, previous.game.moves.length);
        return { ...previous, position: nextPosition, playing: nextPosition < previous.game.moves.length };
      });
    }, REPLAY_MOVE_INTERVAL_MS);
    return () => window.clearTimeout(timeout);
  }, [replaySession, replayPosition, replayPlaying, replayMoveCount]);

  useEffect(() => {
    const pauseHiddenReplay = () => {
      if (document.hidden) setReplay(previous => previous && previous.playing ? { ...previous, playing: false } : previous);
    };
    document.addEventListener('visibilitychange', pauseHiddenReplay);
    return () => document.removeEventListener('visibilitychange', pauseHiddenReplay);
  }, []);

  function playerForSide(side) {
    if (!game || !game.players || !game.players[side]) return null;
    const configured = game.players[side];
    if (!replay && state.activePlayer && (state.activePlayer.side ? state.activePlayer.side === side : side === turn)) return { ...configured, ...state.activePlayer };
    if (!replay) return configured;
    const sideMoves = moves.filter((move, index) => index % 2 === (side === 'w' ? 0 : 1));
    const nextMove = sideMoves.find(move => Number(move.ply) > position);
    const pastMoves = sideMoves.filter(move => Number(move.ply) <= position);
    const historicalMove = side === turn ? nextMove || pastMoves[pastMoves.length - 1] : pastMoves[pastMoves.length - 1] || nextMove;
    return historicalMove && historicalMove.model ? { ...configured, model: historicalMove.model } : configured;
  }
  const whitePlayer = playerForSide('w');
  const blackPlayer = playerForSide('b');
  const turnPlayer = turn === 'b' ? blackPlayer : whitePlayer;
  const turnMessage = !game ? 'Waiting for the server' : replay ? position === moves.length && game.status === 'finished' ? `Final position${game.result ? ` · ${game.result}` : ''}` : 'Reviewing recorded position' : status === 'finished' ? 'Game complete' : `${formatModel(turnPlayer && turnPlayer.model)} is considering a move`;

  function startReplay(recordedGame, selectedPosition = 0, autoplay = true) {
    if (!recordedGame || !Array.isArray(recordedGame.moves)) return;
    const nextPosition = Math.max(0, Math.min(selectedPosition, recordedGame.moves.length));
    replaySequence.current += 1;
    setReplay({ game: recordedGame, position: nextPosition, playing: autoplay && nextPosition < recordedGame.moves.length && !document.hidden, session: replaySequence.current });
    if (autoplay && boardColumnRef.current && typeof boardColumnRef.current.scrollIntoView === 'function') {
      const reducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      boardColumnRef.current.scrollIntoView({ block: 'start', behavior: reducedMotion ? 'auto' : 'smooth' });
    }
  }
  function selectPosition(selectedPosition) {
    if (replay) setReplay(previous => previous ? { ...previous, position: Math.max(0, Math.min(selectedPosition, previous.game.moves.length)), playing: false } : previous);
    else startReplay(state, selectedPosition, false);
  }
  function togglePlayback() {
    setReplay(previous => {
      if (!previous || !previous.game.moves.length) return previous;
      if (previous.playing) return { ...previous, playing: false };
      return { ...previous, position: previous.position >= previous.game.moves.length ? 0 : previous.position, playing: !document.hidden };
    });
  }
  function toggleHints() {
    const enabled = !hintsEnabled;
    setHintsEnabled(enabled);
    try { window.localStorage.setItem(BOARD_HINTS_STORAGE_KEY, String(enabled)); }
    catch (error) { /* Hints still work when browser storage is unavailable. */ }
  }
  return (
    <section className={`chess-lounge ${replay ? 'replaying-game' : ''}`} aria-label="Chess match">
      <h2 className="sr-only">Chess match</h2>
      <div className="lounge-grid">
        <div className="board-column" ref={boardColumnRef}>
          <AgentCard side="b" player={blackPlayer} active={!!game && turn === 'b'} status={status} captures={captures.b} />
          <div className="board-status"><span>{game ? `GAME ${game.gameId.slice(0, 8).toUpperCase()}` : 'JOINING THE ROOM'}</span><span><span className={`connection-dot ${!replay && connection === 'live' ? 'connection-live' : ''}`} /> {replay ? 'Replay' : connection === 'live' ? 'Live' : connection === 'polling' ? 'Connected' : connection === 'reconnecting' ? 'Reconnecting' : connection === 'offline' ? 'Server unavailable' : 'Connecting'}</span></div>
          <ChessBoard fen={fen} lastMove={lastMove} turn={turn} hasState={!!game} thinking={status === 'thinking'} replaying={!!replay} gameId={game && game.gameId} position={position} moves={moves} initialFen={initialFen} connection={connection} hintsEnabled={hintsEnabled} onHintsChange={toggleHints} />
          <div className="board-caption" role="status"><span className="turn-indicator">{turnMessage}</span></div>
          <AgentCard side="w" player={whitePlayer} active={!!game && turn === 'w'} status={status} captures={captures.w} />
          {replay ? <ReplayControls position={position} moves={moves} playing={replayPlaying} onTogglePlayback={togglePlayback} onPositionChange={selectPosition} onReturnLive={() => setReplay(null)} /> : game ? <button className="text-button replay-current-button" onClick={() => startReplay(game)}><FiRotateCcw aria-hidden="true" />Replay this game</button> : null}
          {replay ? <p className="replay-game-detail">Recorded {game.startedAt ? new Date(game.startedAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : 'game'}{game.result ? ` · ${game.result}` : ''}</p> : unavailable || !state || connection === 'reconnecting' || ['error', 'waiting_for_key', 'rate_limited', 'finished'].includes(status) ? <p className="room-status room-status-paused" role="status">{statusMessage(state, connection, unavailable)}</p> : null}
        </div>
        <div className="game-sidebar"><RoomStats stats={state && state.roomStats} position={position} gameMoveCount={moves.length} moveNumber={Number(fen.split(' ')[5]) || 1} replaying={!!replay} balance={balance} hasGame={!!game} /><MoveLog moves={moves} liveGameId={state && state.gameId} hasState={!!game} replay={replay} selectedPosition={position} onSelectMove={selectPosition} onReplayGame={startReplay} /></div>
      </div>
    </section>
  );
}
